import React, { useEffect } from "react";
import { Container } from "../../../components";
import { Icon, Text, Heading, Select } from "@telia/teddy";
import _get from "lodash/get";
import _size from "lodash/size";
import { connect, useDispatch } from "react-redux";
import { FORM_ID } from "../../../../../constants";
import { RootState, Form } from "../../../../../models";
import { getFeatureToggles } from "../../../app/state/selectors";
import { PhoneNumberFormatters } from "../../../app/utils";
import {
    setCanSendTransferOrder,
    setSelectedPhoneNumber,
} from "../../../checkout/state/actions/transferOwnershipActions";
import {
    getTransferExistingIndividualLegalOwnerFullName,
    getHasSentTransferOrder,
    hasExistingLegalOwnerGivenConsent as hasExistingLegalOwnerGivenConsentSelector,
    getTransferDate,
    getTransferExistingOrganizationName,
    getTransferExistingOrganizationAdminPhoneNumbers,
} from "../../../checkout/state/selectors/accountSelectors";
import { getFormById } from "../../../checkout/state/selectors/formSelectors";
import { getFormattedPhoneNumber } from "../../../rapidform/formValidation";
import NumberField from "../../components/molecules/NumberField/NumberField";
import TEXT from "../../text-consts";
import "./RequestTransferOfOwnership.less";

interface StateProps {
    hasExistingLegalOwnerGivenConsent: boolean;
    hasSentTransferOrder: boolean;
    individualFullName: string;
    isNewCommonLandingPageEnabled?: boolean;
    organizationAdminPhoneNumbers: string[] | null;
    organizationName: string;
    requestedTransferDate?: string;
    transferOwnershipForm: Form;
}

type RequestTransferOwnershipProps = StateProps;

const getDescription = (
    isB2BTransfer: boolean,
    individualFullName: string,
    organizationName: string,
    organizationAdminPhoneNumbers: string[] | null,
    isNewCommonLandingPageEnabled?: boolean,
) => {
    if (!isB2BTransfer) {
        return TEXT.REQUEST_TRANSFER_OF_OWNERSHIP.DESCRIPTION_B2C(individualFullName);
    }

    if (_size(organizationAdminPhoneNumbers) === 0) {
        return isNewCommonLandingPageEnabled
            ? TEXT.REQUEST_TRANSFER_OF_OWNERSHIP.DESCRIPTION_B2B_UNKNOWN_ADMIN(organizationName)
            : TEXT.REQUEST_TRANSFER_OF_OWNERSHIP.NO_COMPANY_ADMINS(organizationName);
    }

    return TEXT.REQUEST_TRANSFER_OF_OWNERSHIP.DESCRIPTION_B2B(organizationName);
};

const RequestTransferOwnership: React.FC<RequestTransferOwnershipProps> = ({
    // Redux (data)
    individualFullName,
    isNewCommonLandingPageEnabled,
    organizationName,
    organizationAdminPhoneNumbers,
    hasSentTransferOrder,
    hasExistingLegalOwnerGivenConsent,
    requestedTransferDate,
    transferOwnershipForm,
}) => {
    const dispatch = useDispatch();

    const isB2BTransfer = !!organizationName;
    const selectedPhoneNumber: string = _get(transferOwnershipForm, "values.transfer.number");

    useEffect(() => {
        if (isB2BTransfer && hasExistingLegalOwnerGivenConsent) {
            dispatch(setSelectedPhoneNumber(""));
            dispatch(setCanSendTransferOrder(true));
        } else if (isB2BTransfer && _size(organizationAdminPhoneNumbers) > 0) {
            dispatch(setSelectedPhoneNumber(organizationAdminPhoneNumbers?.[0] || ""));
            dispatch(setCanSendTransferOrder(true));
        }
    }, [isB2BTransfer, organizationAdminPhoneNumbers, hasExistingLegalOwnerGivenConsent]);

    const description = getDescription(
        isB2BTransfer,
        individualFullName,
        organizationName,
        organizationAdminPhoneNumbers,
        isNewCommonLandingPageEnabled,
    );

    const isValidNumber = (number: string) => {
        const onlyDigits = number.replace(/\D/g, "");
        return onlyDigits.length === 8;
    };

    const handleSetSelectedPhoneNumber = (number: string) => {
        const onlyDigits = number.replace(/\D/g, "");
        if (onlyDigits.length <= 8) {
            dispatch(setSelectedPhoneNumber(onlyDigits));
            dispatch(setCanSendTransferOrder(isValidNumber(onlyDigits)));
        }
    };

    function renderB2BFields() {
        if (_size(organizationAdminPhoneNumbers) > 0) {
            const numbers = organizationAdminPhoneNumbers || [];
            return (
                <Select
                    name="request-transfer-of-ownership-number"
                    label={TEXT.REQUEST_TRANSFER_OF_OWNERSHIP.NUMBER_FIELD.LABEL_TEXT}
                    options={numbers}
                    value={(selectedPhoneNumber || "").replace(/\s/g, "")}
                    onValueChange={(value) => {
                        dispatch(setSelectedPhoneNumber(value));
                        return value;
                    }}
                    className="request-transfer-of-ownership__select"
                >
                    <Select.Trigger data-di-mask className="request-transfer-of-ownership__select-trigger">
                        <Select.Value />
                    </Select.Trigger>
                    <Select.Content>
                        <Select.Viewport>
                            {numbers.map((number) => (
                                <Select.Item key={number} value={number}>
                                    {PhoneNumberFormatters[0].displayFn(number)}
                                </Select.Item>
                            ))}
                        </Select.Viewport>
                    </Select.Content>
                </Select>
            );
        }
    }

    function renderB2CFields() {
        return (
            <NumberField
                selectedPhoneNumber={getFormattedPhoneNumber(selectedPhoneNumber)}
                setSelectedPhoneNumber={handleSetSelectedPhoneNumber}
                labelText={TEXT.REQUEST_TRANSFER_OF_OWNERSHIP.NUMBER_FIELD.LABEL_TEXT}
                placeholder={TEXT.REQUEST_TRANSFER_OF_OWNERSHIP.NUMBER_FIELD.PLACEHOLDER}
                data-di-mask
            />
        );
    }

    function renderValidityDescription() {
        let validityDescription = "";

        if (!isB2BTransfer) {
            validityDescription = TEXT.REQUEST_TRANSFER_OF_OWNERSHIP.ORDER_VALIDITY_DESCRIPTION_B2C(individualFullName);
        } else if (isB2BTransfer && _size(organizationAdminPhoneNumbers) > 0) {
            validityDescription = TEXT.REQUEST_TRANSFER_OF_OWNERSHIP.ORDER_VALIDITY_DESCRIPTION_B2B;
        } else {
            validityDescription = TEXT.REQUEST_TRANSFER_OF_OWNERSHIP.ORDER_VALIDITY_DESCRIPTION_GENERAL;
        }

        return (
            <span className="request-transfer-of-ownership__order-text" data-di-mask>
                {validityDescription}
            </span>
        );
    }

    function renderConsentGivenByAdmin(transferDate: string) {
        return (
            <Text mb="100" data-di-mask>
                {TEXT.REQUEST_TRANSFER_OF_OWNERSHIP.CONSENT_GIVEN_BY_COMPANY_ADMINS(organizationName, transferDate)}
            </Text>
        );
    }

    if (hasSentTransferOrder) {
        return (
            <Container size="medium" className="request-transfer-of-ownership" noMargin>
                <Icon name="check-circle-filled" className="request-transfer-of-ownership__check-icon" />
                <Heading mb="100" as="h2" variant="title-200">
                    {hasExistingLegalOwnerGivenConsent
                        ? TEXT.REQUEST_TRANSFER_OF_OWNERSHIP.ORDER_CONFIRMATION.HEADING_PRE_APPROVED
                        : TEXT.REQUEST_TRANSFER_OF_OWNERSHIP.ORDER_CONFIRMATION.HEADING}
                </Heading>
                <div
                    className="paragraph"
                    dangerouslySetInnerHTML={{
                        __html: TEXT.REQUEST_TRANSFER_OF_OWNERSHIP.ORDER_CONFIRMATION.DESCRIPTION,
                    }}
                ></div>
            </Container>
        );
    }

    return (
        <Container size="medium" className="request-transfer-of-ownership" noMargin>
            <Heading mb="100" as="h2" variant="title-200">
                {TEXT.REQUEST_TRANSFER_OF_OWNERSHIP.HEADING}
            </Heading>
            {!hasExistingLegalOwnerGivenConsent && <Text mb="100">{description}</Text>}
            <div className="request-transfer-of-ownership__button-container">
                {hasExistingLegalOwnerGivenConsent &&
                    requestedTransferDate &&
                    renderConsentGivenByAdmin(requestedTransferDate)}
                {isB2BTransfer && !hasExistingLegalOwnerGivenConsent && renderB2BFields()}
                {!isB2BTransfer && renderB2CFields()}
                {isB2BTransfer &&
                    !hasExistingLegalOwnerGivenConsent &&
                    _size(organizationAdminPhoneNumbers) < 1 &&
                    isNewCommonLandingPageEnabled &&
                    renderB2CFields()}
                {!hasExistingLegalOwnerGivenConsent && renderValidityDescription()}
            </div>
        </Container>
    );
};

const mapStateToProps = (state: RootState): StateProps => ({
    individualFullName: getTransferExistingIndividualLegalOwnerFullName(state) || "",
    organizationName: getTransferExistingOrganizationName(state) || "",
    organizationAdminPhoneNumbers: getTransferExistingOrganizationAdminPhoneNumbers(state) || null,
    hasSentTransferOrder: getHasSentTransferOrder(state),
    hasExistingLegalOwnerGivenConsent: hasExistingLegalOwnerGivenConsentSelector(state),
    requestedTransferDate: getTransferDate(state),
    transferOwnershipForm: getFormById(state, FORM_ID.TRANSFER_OWNERSHIP),
    isNewCommonLandingPageEnabled: !!getFeatureToggles(state).enableNewCommonLandingPage,
});

export default connect(mapStateToProps)(RequestTransferOwnership);
